/** tm/pages/CompanyListPage.tsx — 업체별 관리 3컬럼 (필터사이드|업체카드240px|상세5탭) */
import { useState, useMemo, useEffect } from 'react';
import type { TMStore } from '../hooks';
import { C, T, R, Badge, StatusBadge, Empty } from '../components/ui';
import { ResizeHandle, useResizable } from '../components/ResizeHandle';
import CompanyDetailPanel from '../components/CompanyDetailPanel';
import RegisterClientModal from '../components/RegisterClientModal';
import MatchFillPanel from '../components/MatchFillPanel';
import { matchFieldsFromCompany, matchProgress } from '../components/CompanyMatchFields';
import * as api from '../api';
import { inConsult } from '../types';
import { allRows, buckets, companyLine, companyStatus, kstToday } from '../flow/flowLogic';

const YEAR_FILTERS:[string,string][] = [['all','전체'],['~2015','~2015'],['2016~2020','2016~20'],['2021~','2021~'],['none','미등록']];
// 2026-10-12 — 「진행중」 필터가 업체 상태 「진행」과 글자가 달라 아무것도 안 걸리던 것 바로잡음(companyStatus = 진행중) · 중단(업체 포기) 추가
const STATUS_FILTERS:[string,string][] = [['all','전체'],['대기','상담 전'],['진행중','진행 중'],['완료','완료'],['보류','보류'],['중단','중단 (업체 포기)']];
const TURN_FILTERS:[string,string][] = [['all','전체'],['over','기한 지남 (내 차례)'],['wait','회신 대기']];
const BIZ_CATEGORIES = ['전체','도소매업','제조업','IT/SW개발업','연구개발업','서비스업','건설업','숙박업','음식점업','기타'];
function normalizeBizType(bt: string): string {
  if (!bt) return '기타';
  if (/제조|생산|가공/.test(bt)) return '제조업';
  if (/연구\s*개발|R&D|연구\s*용역|엔지니어링\s*서비스/i.test(bt)) return '연구개발업';
  if (/IT|SW|소프트|정보|통신|개발업|프로그램|데이터|전산/.test(bt)) return 'IT/SW개발업';
  if (/도소매|판매|유통|무역|수출|수입/.test(bt)) return '도소매업';
  if (/건설|건축|토목|공사|시공|설비/.test(bt)) return '건설업';
  if (/숙박|호텔|모텔|여관|펜션|민박|게스트\s*하우스|리조트|콘도/.test(bt)) return '숙박업';
  if (/음식|식당|요식|카페|커피|외식|주점|제과점|분식|급식/.test(bt)) return '음식점업';
  if (/서비스|컨설팅|대행|용역|교육|광고|디자인/.test(bt)) return '서비스업';
  return '기타';
}

interface Props { store: TMStore; navParams?: {companyId?:string;taskId?:string;watch?:{id:string;name:string}} }   // watch — 대시보드 「공고 나옴 → 업무 만들기」(2026-10-30)

export default function CompanyListPage({ store, navParams }: Props) {
  const { companies, tasks, fps, wms, consultations, taskTemplates, govPrograms, aiPresets } = store;
  const [search, setSearch] = useState('');
  const [stF, setStF] = useState('all');
  const [turnF, setTurnF] = useState('all');   // 업무 차례 (2026-10-12)
  const [regionF, setRegionF] = useState('all');
  const [regionDetailF, setRegionDetailF] = useState('all');
  const [bizF, setBizF] = useState('전체');
  const [bizDetailF, setBizDetailF] = useState('all');
  const [yearF, setYearF] = useState('all');
  const [incompleteOnlyF, setIncompleteOnlyF] = useState(false);
  const [showMatchFill, setShowMatchFill] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [expandedRegion, setExpandedRegion] = useState<string|null>(null);
  const [expandedBiz, setExpandedBiz] = useState<string|null>(null);
  const [selId, setSelId] = useState<string|null>(navParams?.companyId || null);
  const [navTaskId, setNavTaskId] = useState<string|null>(navParams?.taskId || null);
  const [navWatch, setNavWatch] = useState<{id:string;name:string}|null>(navParams?.watch || null);
  useEffect(() => {
    if (navParams?.companyId) { setSelId(navParams.companyId); setNavTaskId(navParams.taskId || null); setNavWatch(navParams.watch || null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navParams?.companyId, navParams?.taskId, navParams?.watch?.id]);

  // 업체 선택 시 최신 업무 로드
  useEffect(() => { if (selId) store.refreshTasks(); }, [selId]);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState('');
  const col1Resize = useResizable({ initialWidth: 260, minWidth: 180, maxWidth: 500 });
  function showToast(msg:string, type?:'success'|'error') {
    setToast((type==='error'?'❌ ':'✓ ')+msg);
    setTimeout(()=>setToast(''), 3000);
  }

  const usedRegions = useMemo(()=>Array.from(new Set(companies.map(c=>c.region).filter(Boolean))),[companies]);
  const usedBizTypes = useMemo(()=>Array.from(new Set(companies.map(c=>c.biz_type).filter(Boolean))),[companies]);

  // 매칭정보 N/9 — 목록 API가 이미 내려준 데이터로 계산(추가 API 호출 없음), 배지·필터·MatchFillPanel 동일 기준
  const progressById = useMemo(() => {
    const map: Record<string, ReturnType<typeof matchProgress>> = {};
    for (const c of companies) map[c.id] = matchProgress(matchFieldsFromCompany(c));
    return map;
  }, [companies]);
  const incompleteCount = useMemo(
    () => companies.filter(c => (progressById[c.id]?.filled ?? 0) < (progressById[c.id]?.total ?? 0)).length,
    [companies, progressById]
  );
  // 엔진(services/govMatch) 자체 결측 통계 — 상단 요약 보조용(선택적, tm-gov.js 결과 그대로 사용)
  const [engineQuality, setEngineQuality] = useState<{ total: number; missing: Record<string, number> } | null>(null);
  useEffect(() => { api.getGovMatchQuality().then(r => setEngineQuality(r.data || null)).catch(() => {}); }, []);
  const topMissingField = useMemo(() => {
    if (!engineQuality) return null;
    const entries = Object.entries(engineQuality.missing).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
    return entries[0] || null;
  }, [engineQuality]);

  // Region hierarchy: { '경기': { count, details: { '수원시': 3, '성남시': 2 } } }
  const regionHierarchy = useMemo(()=>{
    const map: Record<string, { count: number; details: Record<string, number> }> = {};
    for (const c of companies) {
      const r = c.region;
      if (!r) continue;
      if (!map[r]) map[r] = { count: 0, details: {} };
      map[r].count++;
      const rd = c.region_detail;
      if (rd) {
        map[r].details[rd] = (map[r].details[rd] || 0) + 1;
      }
    }
    return map;
  },[companies]);

  // BizType hierarchy (from biz_types array, fallback to biz_type)
  const bizHierarchy = useMemo(()=>{
    const map: Record<string, { count: number; details: Record<string, number> }> = {};
    for (const c of companies) {
      const types = (c.biz_types && c.biz_types.length > 0) ? c.biz_types : [normalizeBizType(c.biz_type || '')];
      for (const bt of types) {
        if (!map[bt]) map[bt] = { count: 0, details: {} };
        map[bt].count++;
      }
    }
    return map;
  },[companies]);

  // V21_DND:1-Da — 드래그앤드롭 상태 + 핸들러
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  async function onCardDrop(e: React.DragEvent, targetIdx: number) {
    e.preventDefault();
    if (dragIdx === null || dragIdx === targetIdx) { setDragIdx(null); setDragOverIdx(null); return; }
    const arr = filtered.slice();
    const item = arr.splice(dragIdx, 1)[0];
    arr.splice(targetIdx, 0, item);
    setDragIdx(null); setDragOverIdx(null);
    try { await api.reorderCompanies(arr.map(x => x.id)); await store.refresh(); }
    catch { /* ignore */ }
  }
  // 업무 차례 — 지금 단계만 본다(flow/flowLogic) · 업체 상태 — 진행중 · 완료 · 중단(업체 포기) · 보류 · 상담 전
  const todayK = kstToday();
  const turnSets = useMemo(() => {
    const b = buckets(allRows(tasks, companies, todayK));
    return { over: new Set(b.overdue.map(r => r.comp.id)), wait: new Set(b.wait.map(r => r.comp.id)) };
  }, [tasks, companies, todayK]);
  const statusOf = useMemo(() => { const m: Record<string, string> = {}; companies.forEach(c => { m[c.id] = companyStatus(c, tasks); }); return m; }, [companies, tasks]);
  const statusCount = (v: string) => (v === 'all' ? companies.length : companies.filter(c => statusOf[c.id] === v).length);
  const filtered = useMemo(()=>companies.filter(c=>{
    if(search&&!c.name.includes(search)&&!(c.biz_no||'').includes(search)&&!(c.ceo||'').includes(search)&&!(c.real_ceo||'').includes(search))return false;
    if(turnF==='over'&&!turnSets.over.has(c.id))return false;
    if(turnF==='wait'&&!turnSets.wait.has(c.id))return false;
    if(stF!=='all'&&statusOf[c.id]!==stF)return false;
    if(regionF!=='all'&&c.region!==regionF)return false;
    if(regionDetailF!=='all'&&(c.region_detail||'')!==regionDetailF)return false;
    if(bizF!=='전체'){
      const types = (c.biz_types && c.biz_types.length > 0) ? c.biz_types : [normalizeBizType(c.biz_type||'')];
      if(!types.includes(bizF))return false;
    }
    if(yearF!=='all'){
      const y=c.established_year;
      if(yearF==='none'){if(y)return false;}
      else if(yearF==='~2015'){if(!y||y>2015)return false;}
      else if(yearF==='2016~2020'){if(!y||y<2016||y>2020)return false;}
      else if(yearF==='2021~'){if(!y||y<2021)return false;}
    }
    if(incompleteOnlyF){
      const p = progressById[c.id];
      if(!p || p.filled >= p.total) return false;
    }
    return true;
  }),[companies,search,stF,turnF,turnSets,statusOf,regionF,regionDetailF,bizF,yearF,incompleteOnlyF,progressById]);

  const sel = companies.find(c=>c.id===selId);

  async function handleUpdate(id:string, patch:Parameters<typeof api.updateCompany>[1]) {
    await store.saveCompany(id, patch);   // 그 업체 한 곳만 갱신(2026-10-26)
  }

  // RegisterClientModal이 내부적으로 API 호출 후 onSave() 콜백
  async function handleSaved() {
    await store.refreshCompanies();
    setShowAdd(false);
    showToast('업체가 등록되었습니다');
  }

  return (
    <div style={{display:'flex',height:'100%',overflow:'hidden'}}>
      {/* 컬럼1: 필터 사이드바 (토글) */}
      {filterOpen&&(
        <div style={{width:185,borderRight:`1px solid ${C.border}`,background:C.bgAlt,display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
          <div style={{padding:'8px 10px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
            <span style={{fontSize:T.xs,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.4px'}}>필터</span>
            <button onClick={()=>setFilterOpen(false)} style={{fontSize:T.xs,padding:'2px 5px',borderRadius:R.sm,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer'}}>✕</button>
          </div>
          <div style={{flex:1,overflowY:'auto'}}>
            <FHead>⏱ 업무 차례</FHead>
            {TURN_FILTERS.map(([v,l])=><FItem key={v} label={l} count={v==='all'?companies.length:v==='over'?turnSets.over.size:turnSets.wait.size} active={turnF===v} onClick={()=>setTurnF(v)}/>)}
            <FHead>📍 지역</FHead>
            <FItem label="전체" active={regionF==='all'} onClick={()=>{setRegionF('all');setRegionDetailF('all');setExpandedRegion(null);}}/>
            {usedRegions.map(r=>{
              const h=regionHierarchy[r];
              const details=h?Object.entries(h.details).sort((a,b)=>b[1]-a[1]):[];
              const isExpanded=expandedRegion===r;
              return <div key={r}>
                <div onClick={()=>{
                  if(regionF===r&&!isExpanded){setRegionF('all');setRegionDetailF('all');}
                  else{setRegionF(r);setRegionDetailF('all');setExpandedRegion(isExpanded?null:r);}
                }} style={{padding:'7px 10px',cursor:'pointer',background:regionF===r?C.accentLt:'transparent',fontSize:11,fontWeight:regionF===r?700:400,color:regionF===r?C.accent:C.text,borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>{details.length>1?(isExpanded?'▼ ':'▶ '):''}{r}</span>
                  <span style={{fontSize:10,color:C.muted}}>{h?.count||0}</span>
                </div>
                {isExpanded&&regionF===r&&details.map(([d,cnt])=>(
                  <FItem key={d} label={`  ${d}`} active={regionDetailF===d} onClick={()=>setRegionDetailF(regionDetailF===d?'all':d)}/>
                ))}
              </div>;
            })}
            <FHead>🏭 업종</FHead>
            {BIZ_CATEGORIES.map(b=>{
              const h=bizHierarchy[b];
              return <div key={b}>
                <div onClick={()=>{
                  if(b==='전체'){setBizF('전체');return;}
                  setBizF(bizF===b?'전체':b);
                }} style={{padding:'7px 10px',cursor:'pointer',background:bizF===b?C.accentLt:'transparent',fontSize:11,fontWeight:bizF===b?700:400,color:bizF===b?C.accent:C.text,borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span>{b}</span>
                  <span style={{fontSize:10,color:C.muted}}>{h?.count||''}</span>
                </div>
              </div>;
            })}
            <FHead>📊 상태</FHead>
            {STATUS_FILTERS.map(([v,l])=><FItem key={v} label={l} count={statusCount(v)} active={stF===v} onClick={()=>setStF(v)}/>)}
            <FHead>📅 설립연도</FHead>
            {YEAR_FILTERS.map(([v,l])=><FItem key={v} label={l} active={yearF===v} onClick={()=>setYearF(v)}/>)}
          </div>
        </div>
      )}

      {/* 컬럼2: 업체 카드 목록 (240px) */}
      <div style={{width:col1Resize.width,borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bgAlt,flexShrink:0}}>
        <div style={{padding:'8px 10px',borderBottom:`1px solid ${C.border}`,background:C.white,flexShrink:0}}>
          <div style={{display:'flex',gap:4,marginBottom:6}}>
            <button onClick={()=>setFilterOpen(!filterOpen)} style={{padding:'5px 7px',borderRadius:6,border:`1px solid ${filterOpen?C.accent:C.border}`,background:filterOpen?C.accentLt:C.white,fontSize:T.xs,cursor:'pointer',color:filterOpen?C.accent:C.muted,fontWeight:filterOpen?700:400,flexShrink:0}}>🔽</button>
            <input value={search} onChange={e=>{setSearch(e.target.value);setSelId(null);}}
              placeholder="🔍 업체명·사업자번호"
              style={{flex:1,padding:'5px 10px',borderRadius:R.md,border:`1px solid ${C.border}`,fontSize:11,outline:'none',boxSizing:'border-box'}}/>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:T.xs,color:C.muted}}>{filtered.length}건</span>
            <button onClick={()=>{setShowAdd(true);setSelId(null);}}
              style={{fontSize:T.xs,padding:'4px 10px',borderRadius:R.sm,border:'none',background:C.accent,color:'#fff',cursor:'pointer',fontWeight:700}}>+ 업체 등록</button>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:4,marginTop:6,paddingTop:6,borderTop:`1px dashed ${C.border}`}}>
            <label style={{display:'flex',alignItems:'center',gap:4,fontSize:10,color:C.muted,cursor:'pointer'}}>
              <input type="checkbox" checked={incompleteOnlyF} onChange={e=>setIncompleteOnlyF(e.target.checked)} style={{margin:0,cursor:'pointer'}}/>
              매칭정보 미완성만{incompleteCount>0&&<b style={{color:'#D97706',fontWeight:700}}>&nbsp;({incompleteCount})</b>}
            </label>
            <button onClick={()=>setShowMatchFill(true)}
              style={{fontSize:10,padding:'3px 8px',borderRadius:R.sm,border:`1px solid ${C.accent}`,background:'#fff',color:C.accent,cursor:'pointer',fontWeight:700,whiteSpace:'nowrap'}}>📝 매칭정보 채우기</button>
          </div>
          {topMissingField&&(
            <div style={{fontSize:9,color:C.muted,marginTop:4}}>참고(엔진 기준) 최다 결측: {topMissingField[0]} {topMissingField[1]}건</div>
          )}
        </div>
        <div style={{flex:1,overflowY:'auto',padding:8}}>
          {filtered.map((c, idx)=>{
            const act=selId===c.id;
            const coTasks=tasks.filter(t=>t.company_id===c.id);
            // V21_DND:1-Db — draggable 카드
            const isDragging = dragIdx === idx;
            const isDragOver = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
            return(
              <div key={c.id}
                draggable
                onDragStart={(e)=>{setDragIdx(idx);e.dataTransfer.effectAllowed='move';}}
                onDragEnter={()=>{if(dragIdx!==null&&dragIdx!==idx)setDragOverIdx(idx);}}
                onDragOver={(e)=>{e.preventDefault();e.dataTransfer.dropEffect='move';}}
                onDrop={(e)=>onCardDrop(e, idx)}
                onDragEnd={()=>{setDragIdx(null);setDragOverIdx(null);}}
                onClick={()=>{setSelId(act?null:c.id);setShowAdd(false);}}
                style={{padding:'10px 12px',borderRadius:R.md,marginBottom:6,cursor:'grab',border:`2px solid ${act?C.accent:C.border}`,background:act?C.accentLt:C.white,opacity:isDragging?0.4:1,borderTop:isDragOver?`3px solid ${C.accent}`:`2px solid ${act?C.accent:C.border}`,transition:'opacity .15s'}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontSize:T.md,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,marginRight:4}}>{c.name}</span>
                  <StatusBadge status={statusOf[c.id]}/>
                </div>
                {(() => {
                  const ln = companyLine(c, coTasks, todayK);
                  return ln ? <div style={{display:'flex',alignItems:'center',gap:5,fontSize:T.xs,fontWeight:700,color:ln.color,marginBottom:5,overflow:'hidden',whiteSpace:'nowrap',textOverflow:'ellipsis'}}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:ln.color,flexShrink:0}}/>{ln.text}
                  </div> : null;
                })()}
                <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  {c.region&&<Badge>{c.region}</Badge>}
                  {c.biz_types && c.biz_types.length > 0
                    ? <Badge bg='#EEEDFE' color='#3C3489'>{c.biz_types[0]}{c.biz_types.length > 1 ? ` +${c.biz_types.length - 1}` : ''}</Badge>
                    : c.biz_type && <Badge bg='#F3F4F6' color='#6B7280'>{normalizeBizType(c.biz_type)}</Badge>}
                  {(c.fps||[]).length>0&&<Badge bg={C.greenLt} color={C.green}>{c.fps[0].name} FP</Badge>}
                  {(() => {
                    const p = progressById[c.id];
                    if (!p) return null;
                    const complete = p.filled >= p.total;
                    return (
                      <span title={complete?'매칭 정보 모두 입력됨':`미입력: ${p.missing.join(', ')}`}
                        style={{display:'inline-block',padding:'3px 9px',borderRadius:R.sm,fontSize:T.xs,fontWeight:700,whiteSpace:'nowrap',
                          background:complete?C.greenLt:'#F3F4F6',color:complete?C.green:'#9CA3AF'}}>
                        매칭정보 {p.filled}/{p.total}
                      </span>
                    );
                  })()}
                </div>

              </div>
            );
          })}
          {filtered.length===0&&<Empty icon="🏢" message="업체가 없습니다"/>}
        </div>
      </div>

      <ResizeHandle onMouseDown={col1Resize.onMouseDown} />

      {/* 컬럼3: 상세 패널 or 등록 모달 */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!sel&&!showAdd&&(
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:C.muted,fontSize:13,flexDirection:'column',gap:8}}>
            <span style={{fontSize:32}}>👈</span>업체를 선택하거나 등록하세요
          </div>
        )}
        {sel&&!showAdd&&(
          <CompanyDetailPanel
            company={sel}
            tasks={tasks.filter(t=>t.company_id===sel.id)}
            consultations={consultations.filter(c=>inConsult(c, sel.id))}
            fps={fps} govPrograms={govPrograms} taskTemplates={taskTemplates} taskCategories={store.taskCategories} aiPresets={aiPresets}
            onClose={()=>{setSelId(null);setNavTaskId(null);setNavWatch(null);}} onUpdate={handleUpdate} onToast={showToast} onRefresh={async()=>{await store.refresh();setNavTaskId(null);}} initialTaskId={navTaskId} initialWatch={navWatch}
          />
        )}
      </div>

      {/* 업체 등록 모달 (RegisterClientModal이 fixed overlay로 렌더) */}
      {showAdd&&(
        <RegisterClientModal
          fps={fps}
          wms={wms}
          onSave={handleSaved}
          onClose={()=>setShowAdd(false)}
          onToast={showToast}
          onRefresh={store.refresh}
          companies={companies}
        />
      )}

      {/* 매칭정보 일괄 채우기 */}
      {showMatchFill&&(
        <MatchFillPanel
          companies={companies}
          onClose={()=>setShowMatchFill(false)}
          onUpdate={handleUpdate}
          onToast={showToast}
        />
      )}

      {toast&&<div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',padding:'12px 24px',borderRadius:R.md,background:C.text,color:'#fff',fontSize:T.sm,fontWeight:600,zIndex:9999}}>{toast}</div>}
    </div>
  );
}

function dDayN(d:string|null) { if(!d)return null; const _t=new Date(d);_t.setHours(0,0,0,0);const _n=new Date();_n.setHours(0,0,0,0);return Math.round((_t.getTime()-_n.getTime())/86400000); }
function FHead({children}:{children:string}) { return<div style={{padding:'8px 10px',fontSize:T.xs,fontWeight:700,color:C.muted,textTransform:'uppercase',borderBottom:`1px solid ${C.border}`,background:C.bg}}>{children}</div>; }
function FSec({label,items,value,onChange}:{label:string;items:string[];value:string;onChange:(v:string)=>void}) {
  return<><FHead>{label}</FHead><FItem label="전체" active={value==='all'} onClick={()=>onChange('all')}/>{items.map(r=><FItem key={r} label={r} active={value===r} onClick={()=>onChange(value===r?'all':r)}/>)}</>;
}
function FItem({label,active,onClick,count}:{label:string;active:boolean;onClick:()=>void;count?:number}) {
  return<div onClick={onClick} style={{padding:'7px 10px',cursor:'pointer',background:active?C.accentLt:'transparent',fontSize:11,fontWeight:active?700:400,color:active?C.accent:C.text,borderBottom:`1px solid ${C.border}`,display:'flex',justifyContent:'space-between'}}><span>{label}</span>{count!==undefined&&<span style={{fontSize:10,color:C.muted,fontVariantNumeric:'tabular-nums'}}>{count}</span>}</div>;
}
